/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import React, { Fragment, type FunctionComponent } from 'react'

import { EuiCheckableCard, EuiFormFieldset, EuiSpacer, EuiText, EuiTitle } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import { type BudgetConfigurationValues, ScopeType } from '../../../../types'

import CloudResource from './CloudResource'

const CHECKABLE_CARD_MAX_WIDTH = 520

const CardContent = ({
  title,
  description,
}: {
  title: React.ReactNode
  description: React.ReactNode
}) => (
  <Fragment>
    <EuiTitle size='xs'>
      <h4>{title}</h4>
    </EuiTitle>
    <EuiSpacer size='xs' />
    <EuiText size='s' color='subdued'>
      {description}
    </EuiText>
  </Fragment>
)

type Props = {
  cloudResourceRef: React.RefObject<HTMLDivElement>
  errors: Partial<Record<keyof BudgetConfigurationValues, string>>
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  isProcessing: boolean
  scopeRef: React.RefObject<HTMLDivElement>
  values: BudgetConfigurationValues
}

const Scope: FunctionComponent<Props> = ({
  cloudResourceRef,
  errors,
  handleChange,
  isProcessing,
  scopeRef,
  values,
}) => {
  const { scopeType = '' } = values

  const isOrganizationSelected = scopeType === ScopeType.Organization
  const isCloudResourceSelected = scopeType === ScopeType.CloudResource
  const isDisabled = isProcessing

  return (
    <div ref={scopeRef}>
      <EuiFormFieldset
        legend={{
          children: (
            <EuiText size='s' color='subdued'>
              <FormattedMessage
                id='billing.notifications.budgets.budget-settings.scope.description'
                defaultMessage='Choose the scope for your budget. You can apply it to the entire organization or select specific resources.'
              />
            </EuiText>
          ),
        }}
      >
        <EuiSpacer size='s' />

        <EuiCheckableCard
          id='organization-scope-checkable-card'
          css={css({ maxWidth: CHECKABLE_CARD_MAX_WIDTH })}
          checked={isOrganizationSelected}
          data-test-subj='billing.notifications.budgets.budget-settings.scope.organization.checkable-card'
          disabled={isDisabled}
          label={
            <CardContent
              title={
                <FormattedMessage
                  id='billing.notifications.budgets.budget-settings.scope.organization.title'
                  defaultMessage='Organization'
                />
              }
              description={
                <FormattedMessage
                  id='billing.notifications.budgets.budget-settings.scope.organization.description'
                  defaultMessage='Track cost incurred to the entire organization. Includes resources created in the future.'
                />
              }
            />
          }
          onChange={handleChange}
          value={ScopeType.Organization}
        />

        <EuiSpacer size='m' />

        <EuiCheckableCard
          id='cloud-resource-scope-checkable-card'
          css={css({ maxWidth: CHECKABLE_CARD_MAX_WIDTH })}
          checked={isCloudResourceSelected}
          data-test-subj='billing.notifications.budgets.budget-settings.scope.cloud-resource.checkable-card'
          disabled={isDisabled}
          label={
            <CardContent
              title={
                <FormattedMessage
                  id='billing.notifications.budgets.budget-settings.scope.cloud-resource.title'
                  defaultMessage='Cloud resource'
                />
              }
              description={
                <FormattedMessage
                  id='billing.notifications.budgets.budget-settings.scope.cloud-resource.description'
                  defaultMessage='Narrow the set of Cloud resources to a single instance.'
                />
              }
            />
          }
          onChange={handleChange}
          value={ScopeType.CloudResource}
        >
          {isCloudResourceSelected && (
            <CloudResource
              cloudResourceRef={cloudResourceRef}
              errors={errors}
              handleChange={handleChange}
              isProcessing={isProcessing}
              values={values}
            />
          )}
        </EuiCheckableCard>
      </EuiFormFieldset>
    </div>
  )
}

export default Scope
